/**
 * Fixed game ordering for the diagnostic.
 *
 * Students no longer choose which game hosts each phase — the platform assigns
 * one deterministically by phase number using this list. Phase N maps to index
 * N-1. The run is 10 stages (6 questions each); this list holds one game per
 * stage so the placement samples a different game each round. Which game sits in
 * which slot is cosmetic — in flat mode the server picks questions by grade band
 * (`STAGE_GRADE_BANDS`), not by game. `blaster-gate` stays first (it hosts the
 * deterministic Phase-1 anchor sweep and is the default slug). Any phase beyond
 * the list falls back to the first game.
 *
 * Every slug here must be a published (live), flat-capable game (see
 * `useDiagnosticGames`) or the diagnostic phase will 400 on select-game.
 */
export const DIAGNOSTIC_GAME_ORDER = [
  "blaster-gate", // stage 1 — anchor sweep (must stay first)
  "shooting-gallery", // stage 2
  "summit-scramble", // stage 3
  "boxing", // stage 4
  "treasure-dig", // stage 5
  "dino-parade", // stage 6
  "scoop-squad", // stage 7
  "grand-prix", // stage 8
  "fizz-lab", // stage 9
  "math-strikers", // stage 10
] as const;

/** Resolve which game hosts a given 1-indexed diagnostic phase. */
export function gameForPhase(phaseNumber: number): string {
  return DIAGNOSTIC_GAME_ORDER[phaseNumber - 1] ?? DIAGNOSTIC_GAME_ORDER[0]!;
}

export type GameOrientation = "portrait" | "landscape";

/**
 * Orientation each diagnostic game is designed for. Mirrors the
 * `deviceRequirements.preferredOrientation` field authored in each game's
 * manifest.json — kept local so the shell's rotate gate doesn't need a
 * manifest fetch per phase.
 */
export const DIAGNOSTIC_GAME_ORIENTATION: Record<string, GameOrientation> = {
  "blaster-gate": "portrait",
  "shooting-gallery": "portrait",
  "summit-scramble": "portrait",
  boxing: "portrait",
  "treasure-dig": "portrait",
  "dino-parade": "portrait",
  "garden-party": "portrait",
  "scoop-squad": "portrait",
  "grand-prix": "portrait",
  "fizz-lab": "portrait",
  "math-strikers": "portrait",
};

/** Resolve the orientation a diagnostic game expects (portrait when unknown). */
export function orientationForGame(slug: string): GameOrientation {
  return DIAGNOSTIC_GAME_ORIENTATION[slug] ?? "portrait";
}
